// client/src/index.js
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "./i18n";
import App from "./app/App";
import "./styles/index.css";
import { setProxyBearer } from "./api/proxy";
import { fetchSessionToken } from "./api/auth";

// ── Sentry error tracking ─────────────────────────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || "";
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || "production",
    release: import.meta.env.VITE_APP_VERSION || "unknown",
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
    beforeSend(event) {
      // Don't send events for suppressed network errors
      const msg = event.exception?.values?.[0]?.value || "";
      if (/failed to fetch|network|load/i.test(msg)) return null;
      return event;
    },
  });
}

// ✅ Show what API base the app will use
const EDGE_ORIGIN = import.meta.env.VITE_EDGE_ORIGIN || "";

// GHL SSO token (from iframe embed)
const ghlSsoToken = new URLSearchParams(window.location.search).get('ghl_sso');
if (ghlSsoToken) {
  console.info('[embed] GHL SSO token detected — will exchange for session when SSO is implemented');
}

// Auth: try session token first, fall back to static bearer
const PROXY_BEARER = (import.meta.env.VITE_PROXY_BEARER || "").trim();
const locationId = new URLSearchParams(window.location.search).get("locationId") || "";
if (locationId) {
  fetchSessionToken(locationId).then((token) => {
    if (token) {
      console.info("[auth] Session token acquired for", locationId);
    } else if (PROXY_BEARER) {
      setProxyBearer(PROXY_BEARER);
      console.info("[auth] Falling back to static bearer");
    }
  });
} else if (PROXY_BEARER) {
  setProxyBearer(PROXY_BEARER);
  console.info("[auth] Using static bearer (no locationId in URL)");
}

// Suppress noisy "Failed to fetch" overlay in dev — these are handled in-app
window.addEventListener("unhandledrejection", (e) => {
  const msg = String(e?.reason?.message || e?.reason || "");
  if (/failed to fetch|network|load/i.test(msg)) {
    console.warn("[unhandledrejection suppressed]", msg);
    e.preventDefault();
  }
});

// Ping health — legacy proxy requires bearer
try {
  const u = new URL(EDGE_ORIGIN || window.location.origin);
  u.pathname = (u.pathname.replace(/\/+$/,'') + "/api/health").replace(/\/+/g, "/");
  fetch(u.toString())
    .then(r => console.log("Health:", r.status))
    .catch(e => console.warn("Health check failed:", e.message));
} catch (e) {
  console.warn("Health check URL build failed:", e);
}

const root = createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);
